import { Box, Button, MenuItem, Select, Typography, useMediaQuery, useTheme } from '@mui/material';
import { FeaturedBox, FilterContainer } from 'src/styledComponents/styledBoxs';
import React, { useMemo } from 'react';
import { categories, projects } from '../../resources/data/featuredWorkData';

import FilterListIcon from '@mui/icons-material/FilterList';
import ProjectCard from './projectCard';
import { SubHeader } from 'src/styledComponents/styledTypographies';
import { content } from '../../common/lang/en-US';

const FeaturedWork: React.FC = () => {
    const [open, setOpen] = React.useState(false);
    const [selectedCategory, setSelectedCategory] = React.useState<string>('All');
    const theme = useTheme();
    const isMediumScreens = useMediaQuery(theme.breakpoints.down('md'));

    const filteredProjects = useMemo(
        () =>
            selectedCategory === 'All'
                ? projects
                : projects.filter((project) => project.categories.includes(selectedCategory)),
        [selectedCategory],
    );

    const getFilterDesignInMobileView = (): React.ReactNode => (
        <FilterContainer justifyContent="flex-start" marginTop={2}>
            <Typography sx={{ color: 'grey.200', marginRight: 1.5 }} variant="h5">
                {content.common.text.filterBy}
            </Typography>
            <Select
                IconComponent={(props) => (
                    <FilterListIcon {...props} className="filter-icon" onClick={() => setOpen(!open)} />
                )}
                onChange={(e) => setSelectedCategory(e.target.value)}
                onClose={() => setOpen(false)}
                onOpen={() => setOpen(true)}
                open={open}
                style={{
                    borderColor: '5px solid red',
                }}
                sx={{
                    ':hover': { color: 'secondary.main' },
                    ':hover .filter-icon': { color: 'secondary.main' },
                    cursor: 'pointer',
                    minWidth: '12.5rem',
                }}
                value={selectedCategory}
                variant="standard"
            >
                <MenuItem sx={{ display: 'none' }} value={selectedCategory}>
                    <Typography variant="caption1_Thin">{selectedCategory}</Typography>
                </MenuItem>
                {categories
                    .filter((category) => category !== selectedCategory)
                    .map((category) => (
                        <MenuItem
                            key={category}
                            sx={{ color: selectedCategory === category ? 'secondary.main' : 'grey.200' }}
                            value={category}
                        >
                            <Typography variant="caption1_Thin">{category}</Typography>
                        </MenuItem>
                    ))}
            </Select>
        </FilterContainer>
    );

    const getFilterDesignInLargeView = (): React.ReactNode => (
        <FilterContainer>
            <Typography sx={{ color: 'grey.200', marginRight: 1.5 }} variant="h5">
                {content.common.text.filterBy}
            </Typography>
            {categories.map((category, index) => (
                <React.Fragment key={category}>
                    <Button
                        aria-label={`${category}-${content.common.ariaLables.filter}`}
                        disableFocusRipple
                        disableRipple
                        onClick={() => setSelectedCategory(category)}
                        sx={{
                            color: selectedCategory === category ? 'secondary.main' : 'grey.200',
                            padding: 3,
                        }}
                    >
                        <Typography variant="caption1_Thin">{category}</Typography>
                    </Button>
                    {index < categories.length - 1 && (
                        <Typography color="grey.600" variant="caption1">
                            /
                        </Typography>
                    )}
                </React.Fragment>
            ))}
        </FilterContainer>
    );

    return (
        <FeaturedBox id="products" paddingX={{ lg: '10rem', md: '5rem', sm: '3rem', xs: '2rem' }}>
            <Typography
                sx={{
                    mb: { xs: 2, xxl: '60px' }, // Margin bottom
                    mt: { xxl: '80px' }, // Margin top for xxl
                }}
                variant="h2"
            >
                {content.featuredWork.header}
            </Typography>
            <SubHeader variant="h4_Thin">{content.featuredWork.subHeader}</SubHeader>
            {isMediumScreens ? getFilterDesignInMobileView() : getFilterDesignInLargeView()}
            <Box
                display="grid"
                gap={6}
                gridTemplateColumns={{ md: 'repeat(3, 1fr)', sm: 'repeat(2, 1fr)', xs: '1fr' }}
                marginBottom={{ xxl: '2rem' }}
                marginTop="2rem"
            >
                {filteredProjects.map((project) => (
                    <ProjectCard key={project.id} {...project} />
                ))}
            </Box>
        </FeaturedBox>
    );
};

export default FeaturedWork;
